/**
 * Jedyne zrodlo prawdy dla strony /realizacje.
 *
 * Liczby w naglowku sa liczone Z TEJ TABLICY w runtime (patrz RealizacjeGrid),
 * nigdy wpisywane recznie. Powod: GroDev ma w copy "16 projektow", a w kodzie 19
 * kafli - strona, ktorej teza brzmi "nie obiecuje, pokazuje", klamie we wlasnym naglowku.
 *
 * ZASADA TRESCI: opis mowi CO SIE DZIEJE, nie jaki system jest. Zle: "nowoczesny system
 * obslugi klienta AI". Dobrze: "Kupujacy pisze na Allegro. System czyta wiadomosc,
 * sprawdza status zamowienia i kladzie gotowa odpowiedz do zatwierdzenia".
 *
 * ZASADA LICZB: kazda liczba tutaj musi byc potwierdzona w pamieci vaultu. Metryki
 * niepotwierdzone (np. "35h/tydz" BLDG) NIE wchodza dopoki Dawid ich nie potwierdzi.
 */

/** Czy to oddane wdrozenie u kogos, czy wlasny prototyp. Rozdzielamy jawnie. */
export type ProjectStatus = 'wdrozenie' | 'demo';

/**
 * Jak bardzo mozna to dotknac.
 * - `klikalne`  - wchodzisz i uzywasz, zero logowania. Jedyny status, ktory daje link.
 * - `zamkniete` - dziala na produkcji, ale za logowaniem albo na danych klienta.
 */
export type ProjectAccess = 'klikalne' | 'zamkniete';

export interface Project {
  id: string;
  title: string;
  /** Krotka etykieta kategorii nad tytulem. */
  category: string;
  /** 2-3 zdania, mechanistycznie. Co robi uzytkownik, co robi system. */
  description: string;
  status: ProjectStatus;
  access: ProjectAccess;
  /** Tylko dla `access: 'klikalne'`. Zewnetrzne otwieramy w nowej karcie. */
  href?: string;
  /** Jedna twarda liczba z produkcji. Pokazywana wyroznionym drukiem. */
  metric?: string;
  /** Skad ta liczba pochodzi. Buduje wiarygodnosc mocniej niz sama liczba. */
  metricSource?: string;
  /** Dlaczego czegos nie da sie kliknac. Wymagane gdy `access: 'zamkniete'`. */
  closedReason?: string;
  /**
   * Miniatura 16:9 w /images/realizacje/. WebP, szerokosc 800px, lazy.
   * Zrzuty robi `npm run shots:realizacje` (scripts/shoot-realizacje.mjs) z ZYWYCH
   * adresow - nie sa to mockupy. Brak pola = kafel bez obrazka (nadal poprawny).
   */
  thumb?: string;
  /** Alt miniatury. Opisuje co widac na ekranie, nie powtarza tytulu. */
  thumbAlt?: string;
  /**
   * Czym JEST miniatura. Domyslnie 'zrzut' - realny ekran dzialajacego systemu.
   *
   * 'ilustracja' = grafika wygenerowana, uzywana TYLKO tam, gdzie system nie ma
   * interfejsu (skrypt z terminala). Kafel oznacza to wtedy widocznie. Bez tego
   * rozroznienia strona lamie wlasna teze "nie opowiadam, pokazuje" - czytelnik
   * ma prawo wiedziec, na co patrzy.
   */
  thumbKind?: 'zrzut' | 'ilustracja';
  stack: string;
}

export const PROJECTS: Project[] = [
  // ---------- WDROZENIA (dziala u kogos, kto tego uzywa) ----------
  {
    id: 'quikz',
    title: 'Autoresponder marketplace',
    category: 'Allegro, eBay, Kaufland, Empik',
    description:
      'Kupujący pisze wiadomość na Allegro. System ją czyta, sprawdza status zamówienia w BaseLinkerze i kładzie gotową odpowiedź do zatwierdzenia. Człowiek czyta i klika wyślij. Cztery marketplace w jednej skrzynce.',
    status: 'wdrozenie',
    access: 'zamkniete',
    closedReason:
      'W środku są realne wiadomości kupujących. Pokazuję sam panel statystyk, bez treści korespondencji.',
    thumb: '/images/realizacje/quikz.webp',
    thumbAlt:
      'Panel statystyk autorespondera: liczba zgłoszeń, nowe z tygodnia, średni czas odpowiedzi i wykres z siedmiu dni',
    // Liczba MUSI zgadzac sie z tym, co widac na zrzucie. Wczesniej stalo tu 15 014
    // (suma per marketplace z pamieci vaultu), a panel pokazuje 25 600 w bazie
    // workspace - inna miara i nowszy stan. Obraz jest dowodem, wiec wygrywa obraz.
    metric: '25 600 obsłużonych zgłoszeń',
    metricSource:
      '1 654 nowych w ostatnim tygodniu, średni czas pierwszej odpowiedzi 41 minut, pięć kanałów w jednej skrzynce. Liczby ze zrzutu obok.',
    stack: 'Next.js, Postgres, Claude, BaseLinker',
  },
  {
    id: 'kognita-coldmail',
    title: 'Pozyskiwanie klientów dla firmy VR',
    category: 'Cold email, dane publiczne',
    description:
      'Skrypt wyciąga adresy szkół z rejestru publicznego, sprawdza które mają budżet na sprzęt, i odpala kampanię mailową. Każda wiadomość pisana pod konkretną placówkę, nie szablon z podmienionym imieniem.',
    status: 'wdrozenie',
    access: 'zamkniete',
    closedReason: 'Pipeline wewnętrzny, uruchamiany z terminala. Nie ma interfejsu do pokazania.',
    thumb: '/images/realizacje/kognita-coldmail.webp',
    thumbAlt:
      'Schemat pipeline: baza rekordów, filtr odsiewający, wychodzące wiadomości, znak Smartlead',
    thumbKind: 'ilustracja',
    metric: '2 000 maili, 2 pozyskanych klientów',
    metricSource: 'Kampania zwróciła się kilkukrotnie. Liczby potwierdzone przez klienta.',
    stack: 'Python, DuckDB, Smartlead',
  },
  {
    id: 'hsglobal',
    title: 'hsglobal.ltd',
    category: 'Strona dwujęzyczna, logistyka',
    description:
      'Strona firmy spedycyjnej po polsku i angielsku. Fracht, odprawa celna, magazynowanie, dostawa ostatniej mili. Wdrożona, oddana, działa na własnej domenie.',
    status: 'wdrozenie',
    access: 'klikalne',
    href: 'https://hsglobal.ltd',
    thumb: '/images/realizacje/hsglobal.webp',
    thumbAlt:
      'Strona HS Global z sekcją główną i licznikami przesyłek',
    stack: 'React, Vite, Netlify',
  },
  {
    id: 'kognita-oferty',
    title: 'System ofertowania i cennika',
    category: 'Katalog produktów, oferty, przetargi',
    description:
      'Handlowiec wybiera produkty z katalogu, system liczy narzut, kursy walut i koszt transportu, po czym generuje gotową ofertę w PDF. Ceny zaciągają się ze stron dostawców same.',
    status: 'wdrozenie',
    access: 'zamkniete',
    closedReason: 'System wewnętrzny firmy, na żywych cenach zakupu i marżach.',
    // Zrzut celowo BEZ kolumn zakupowych i marż - to dane handlowe klienta.
    // Widoczne są tylko ceny sprzedaży, czyli to samo, co w jego sklepie.
    thumb: '/images/realizacje/kognita-oferty.webp',
    thumbAlt: 'Katalog produktów z kategoriami, cenami sprzedaży i stanem magazynu',
    metric: '1 529 produktów w żywym katalogu',
    metricSource: 'Stan bazy produkcyjnej.',
    stack: 'Next.js, Postgres, BaseLinker, LiveSpace',
  },
  {
    id: 'przetargi',
    title: 'Asystent przetargowy',
    category: 'Zamówienia publiczne',
    description:
      'Wrzucasz folder z dokumentacją przetargową. System rozbija ją na części, wyciąga każdy wymóg techniczny osobno i przy każdym stawia werdykt: spełniamy, uwaga albo nie spełniamy. Potem sam dobiera produkty, pyta dostawców o ceny i składa kalkulację. Przed wysyłką druga bramka blokuje ofertę, jeśli któryś parametr nie ma pokrycia w karcie producenta.',
    status: 'wdrozenie',
    access: 'zamkniete',
    closedReason: 'Stoi w sieci prywatnej. W środku leżą realne dokumenty przetargowe i wyceny.',
    thumb: '/images/realizacje/przetargi.webp',
    thumbAlt:
      'Tabela zgodności: wymóg z dokumentacji, nasza deklaracja z karty producenta i werdykt przy każdym parametrze',
    metric: 'Sześć kroków od folderu do oferty gotowej do podpisu',
    metricSource:
      'Dokumentacja, kalkulacja, zapytania do dostawców, eksport arkusza, środki dowodowe, podpis. Na realnym postępowaniu: 9 części, 22 pliki, analiza zgodna co do znaku z dokumentacją źródłową.',
    stack: 'Next.js, Postgres, Claude',
  },

  // ---------- DEMA (buduje sam, zeby pokazac ze umiem) ----------
  {
    id: 'quiz',
    title: 'Kalkulator strat',
    category: 'Narzędzie kwalifikujące',
    description:
      'Odpowiadasz na kilka pytań o swojej firmie. Kalkulator liczy ile rocznie kosztują Cię nieodebrane telefony i leady, do których nikt nie oddzwonił. Wynik dostajesz na ekranie, nie na maila po trzech dniach.',
    status: 'demo',
    access: 'klikalne',
    href: '/quiz',
    thumb: '/images/realizacje/quiz.webp',
    thumbAlt:
      'Ekran kalkulatora liczącego roczne straty firmy',
    stack: 'React, TypeScript',
  },
  {
    id: 'wizytowka-3d',
    title: 'Wizytówka 3D i AR',
    category: 'Model 3D w przeglądarce',
    description:
      'Skanujesz kod QR, w telefonie staje trójwymiarowa wizytówka. Obracasz ją palcem, dotknięcie zapisuje kontakt. Bez instalowania aplikacji, wszystko dzieje się w przeglądarce.',
    status: 'demo',
    access: 'klikalne',
    href: 'https://card-app-snowy-five.vercel.app/daien',
    thumb: '/images/realizacje/wizytowka-3d.webp',
    thumbAlt:
      'Trójwymiarowa wizytówka obracana w przeglądarce',
    stack: 'Next.js, model-viewer, MindAR',
  },
  {
    id: 'okademia',
    title: 'okademia.pl',
    category: 'Zapisy na kurs',
    description:
      'Formularz zapisu na listę oczekujących. Po zapisie mail powitalny wychodzi sam, wpis ląduje w bazie do dalszej komunikacji.',
    status: 'demo',
    access: 'klikalne',
    href: 'https://okademia.pl',
    thumb: '/images/realizacje/okademia.webp',
    thumbAlt:
      'Strona zapisu na listę oczekujących OKademia',
    stack: 'Next.js, Resend, Notion',
  },
  {
    id: 'crm-hvac',
    title: 'CRM dla firmy klimatyzacyjnej',
    category: 'Lejek, wyceny, zlecenia technika',
    description:
      'Lead wpada z reklamy, system go punktuje i przypisuje handlowcowi. Handlowiec robi wycenę z katalogu, klient zatwierdza ją linkiem bez zakładania konta, technik dostaje zlecenie na telefon i zbiera podpis na ekranie.',
    status: 'demo',
    access: 'zamkniete',
    closedReason: 'Panel za logowaniem. Otwarte demo z danymi przykładowymi jest w przygotowaniu.',
    thumb: '/images/realizacje/crm-hvac.webp',
    thumbAlt: 'Tablica leadów w CRM z kolumnami od nowego zgłoszenia do zlecenia',
    metric: '94 endpointy, 189 z 189 testów zielonych',
    metricSource: 'To metryka rzemiosła, nie wyniku u klienta. System czeka na pierwsze wdrożenie.',
    stack: 'Next.js, Postgres, Drizzle',
  },
  {
    id: 'opiniopilot',
    title: 'Zbieranie opinii Google',
    category: 'Po zakończonej usłudze',
    description:
      'Technik kończy zlecenie, klient dostaje SMS z prośbą o opinię. Zadowolony trafia prosto do Google, niezadowolony na formularz do właściciela. Bez bramkowania ocen, zgodnie z regulaminem Google.',
    status: 'demo',
    access: 'zamkniete',
    closedReason: 'Panel za logowaniem. Landing dla klienta końcowego działa po linku z SMS.',
    thumb: '/images/realizacje/opiniopilot.webp',
    thumbAlt: 'Strona, którą klient widzi po SMS: nazwa firmy i przycisk wystawienia opinii',
    stack: 'Next.js, Postgres, Resend, Twilio',
  },
];

/** Kolejnosc grup na stronie. Wdrozenia zawsze pierwsze. */
export const GROUPS: { status: ProjectStatus; heading: string; sub: string }[] = [
  {
    status: 'wdrozenie',
    heading: 'Wdrożenia',
    sub: 'Działa u kogoś, kto z tego korzysta na co dzień.',
  },
  {
    status: 'demo',
    heading: 'Własne prototypy',
    sub: 'Buduję je sam, żeby pokazać co potrafię. Najlepszy dowód to system, który mogę pokazać dziś, a nie referencja pod umową o poufności.',
  },
];
